import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Rating,
  LinearProgress,
  Avatar,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import color from "../../../components/utils/Colors";
import { KeyboardArrowDown } from "@mui/icons-material";
import { courseRatings } from "../../../components/utils/Data";
import { useParams } from "react-router-dom";
import { useUser } from "../../../services/Context/UserContext";
import { getDataFromTable, createUserRating } from "../../../services/services";
import { useSnackbar } from "../../../components/utils/SnackBar";

const ReviewTab = () => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [ratingInput, setRatingInput] = useState<number>(5);
  const [feedbackInput, setFeedbackInput] = useState<string>("");
  const [filterRating, setFilterRating] = useState<string>("All Ratings");
  const [visibleCount, setVisibleCount] = useState<number>(3);
  const { user } = useUser();
  const { courseId } = useParams<{ courseId: string }>();
  const showSnackbar = useSnackbar();

  const fetchReviews = async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await getDataFromTable("userRatings", {
        feedbackType: `course_${courseId}`,
      });
      const rawData = res?.data?.data || [];

      const mapped = rawData.map((item: any) => {
        let name = "Student";
        let text = item.feedback;
        try {
          if (item.feedback) {
            const parsed = JSON.parse(item.feedback);
            if (parsed && typeof parsed === "object") {
              name = parsed.authorName || "Student";
              text = parsed.text || "";
            }
          }
        } catch (e) {
          // not json, display feedback directly
        }

        if (user && item.userId === user.id) {
          name = user.name || name;
        }

        const date = item.createdAt ? new Date(item.createdAt) : new Date();
        const timeStr = date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        return {
          id: item.id,
          name,
          rating: item.rating || 5,
          time: timeStr,
          text: text || "No comment provided.",
        };
      });
      // Sort reviews newest first
      mapped.sort((a: any, b: any) => b.id - a.id);
      setReviews(mapped);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchReviews();
  }, [courseId, user]);

  const totalReviews = reviews.length;
  const averageRating =
    totalReviews > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews).toFixed(
          1,
        )
      : "0.0";

  const starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach((r) => {
    const star = Math.round(r.rating);
    if (star >= 1 && star <= 5) {
      starCounts[star as 5 | 4 | 3 | 2 | 1]++;
    }
  });

  const getPercentageString = (stars: number) => {
    if (totalReviews === 0) return "0%";
    const count = starCounts[stars as 5 | 4 | 3 | 2 | 1] || 0;
    return `${Math.round((count / totalReviews) * 100)}%`;
  };

  const filteredReviews = reviews.filter((r) => {
    if (filterRating === "All Ratings") return true;
    const match = filterRating.match(/^(\d+)/);
    if (match) {
      const ratingVal = parseInt(match[1]);
      return Math.round(r.rating) === ratingVal;
    }
    return true;
  });

  const displayedReviews = filteredReviews.slice(0, visibleCount);

  const handleSubmitFeedback = async () => {
    if (!user?.id) {
      showSnackbar("You must be logged in to leave feedback.", "error");
      return;
    }
    if (!courseId) {
      showSnackbar("Course ID is missing.", "error");
      return;
    }
    if (!feedbackInput.trim()) {
      showSnackbar("Please enter your feedback text.", "warning");
      return;
    }

    try {
      const payload = {
        rating: ratingInput,
        feedback: JSON.stringify({
          authorName: user.name || "Student",
          text: feedbackInput,
        }),
        feedbackType: `course_${courseId}`,
        userId: user.id,
      };
      await createUserRating(payload);
      showSnackbar("Feedback submitted successfully!", "success");
      setFeedbackInput("");
      setRatingInput(5);
      fetchReviews();
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to submit feedback.",
        "error",
      );
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress sx={{ color: color.firstColor }} />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "20px", maxWidth: "800px", margin: "auto" }}>
      <Typography
        sx={{
          color: color.textColor,
          fontWeight: "600",
          fontSize: { xs: "18px", sm: "20px", md: "24px" },
          padding: "30px",
          pl: 0,
        }}
      >
        Student Feedback
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        gap={4}
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
      >
        <Box display="flex" alignItems="center" flexDirection={"column"} mb={2}>
          <Typography
            variant="h2"
            sx={{ fontWeight: "bold", mr: 2, color: color.textColor }}
          >
            {averageRating}
          </Typography>
          <Rating
            sx={{
              color: color.firstColor,
            }}
            value={parseFloat(averageRating)}
            precision={0.1}
            readOnly
          />
          <Typography
            sx={{
              color: color.firstColor,
              fontWeight: "bold",
              mt: 1,
            }}
          >
            Course Rating ({totalReviews}{" "}
            {totalReviews === 1 ? "review" : "reviews"})
          </Typography>
        </Box>

        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          {[5, 4, 3, 2, 1].map((stars) => {
            const percentStr = getPercentageString(stars);
            const numericPercent = parseInt(percentStr);

            return (
              <Box display="flex" alignItems="center" key={stars} mb={1}>
                <LinearProgress
                  variant="determinate"
                  value={numericPercent}
                  sx={{
                    width: { xs: "200px", sm: "300px" },
                    mr: 2,
                    borderRadius: "8px",
                    height: "20px",
                    backgroundColor: "#eee",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: color.firstColor,
                    },
                  }}
                />
                <Rating
                  sx={{
                    color: color.firstColor,
                  }}
                  value={stars}
                  readOnly
                  size="small"
                />
                <Typography sx={{ ml: 2, color: color.firstColor }}>
                  {percentStr}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems={"flex-end"}
        flexDirection={"column"}
        sx={{ mt: 4, ml: "auto" }}
      >
        <Typography
          sx={{
            color: color.firstColor,
            fontWeight: "bold",
            mb: 1,
            width: "200px",
          }}
        >
          Filter:
        </Typography>
        <TextField
          select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
          variant="outlined"
          className="input-root2"
          sx={{ width: "200px", height: "50px" }}
        >
          {courseRatings.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", mt: -2 }}>
        <Typography
          sx={{
            color: color.textColor,
            fontWeight: "600",
            fontSize: { xs: "18px", sm: "20px", md: "24px" },
            padding: "0px",
            mb: 4,
            pl: 0,
          }}
        >
          Reviews
        </Typography>

        {displayedReviews.length === 0 ? (
          <Typography sx={{ color: "gray", fontStyle: "italic", mb: 4 }}>
            No reviews match your selected filter.
          </Typography>
        ) : (
          displayedReviews.map((review) => (
            <Box
              key={review.id}
              display="flex"
              mb={3}
              sx={{
                borderBottom: "solid 1px",
                borderColor: color.borderColor,
                pb: 3,
              }}
            >
              <Avatar alt={review.name} src="/assets/pfp.png" />
              <Box ml={2}>
                <Typography
                  sx={{
                    color: color.firstColor,
                    fontWeight: "bold",
                  }}
                  variant="body1"
                >
                  {review.name}
                </Typography>
                <Box sx={{ display: "flex", mb: 2 }}>
                  <Rating
                    sx={{
                      color: color.firstColor,
                    }}
                    value={review.rating}
                    readOnly
                    size="small"
                  />
                  <Typography
                    sx={{
                      color: color.firstColor,
                      ml: 1,
                    }}
                    variant="caption"
                    display="block"
                  >
                    {review.time}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: color.textColor,
                  }}
                  variant="body2"
                >
                  {review.text}
                </Typography>
              </Box>
            </Box>
          ))
        )}

        {filteredReviews.length > visibleCount && (
          <Button
            id="button"
            onClick={() => setVisibleCount((prev) => prev + 3)}
            style={{
              border: "none",
              margin: "auto",
              marginBottom: "40px",
              marginTop: "30px",
            }}
          >
            View More <KeyboardArrowDown></KeyboardArrowDown>
          </Button>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", mt: 4 }}>
        <Typography
          sx={{
            color: color.textColor,
            fontWeight: "600",
            fontSize: { xs: "18px", sm: "20px", md: "24px" },
            padding: "0px",
            mb: 2,
            pl: 0,
          }}
        >
          Add your feedback here
        </Typography>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography sx={{ color: color.textColor }}>Your Rating:</Typography>
          <Rating
            value={ratingInput}
            onChange={(event, newValue) => {
              if (newValue !== null) setRatingInput(newValue);
            }}
            sx={{ color: color.firstColor }}
          />
        </Box>

        <TextField
          variant="outlined"
          className="input-root2"
          fullWidth
          multiline
          rows={4}
          placeholder="Write your feedback here"
          value={feedbackInput}
          onChange={(e) => setFeedbackInput(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Button
          id="button1"
          style={{
            marginLeft: "auto",
          }}
          onClick={handleSubmitFeedback}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewTab;
